"use client";

import { forwardRef } from "react";

import cn from "classnames";

import { FieldErrorMessage } from "@/components/formError/fieldErrorMessage.component";
import { InfoIcon } from "@/components/icons/info.icon";

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { id, name, label, value, className, error, ...rest },
  ref,
) {
  const resolvedError =
    typeof error === "string"
      ? error
      : error === true
        ? "Please enter a valid value."
        : undefined;

  return (
    <div className="flex w-full flex-col" data-testid="input">
      <div className="flex w-full flex-col gap-1">
        {label ? (
          <label
            data-testid="input-label"
            htmlFor={id}
            className={cn("body-2 mb-[9.5px]", {
              "text-error": resolvedError,
            })}>
            {label}
          </label>
        ) : null}
        <div className="relative">
          <input
            ref={ref}
            data-testid="input-field"
            id={id}
            name={name}
            value={value}
            aria-invalid={resolvedError ? true : undefined}
            aria-describedby={resolvedError && id ? `${id}-error` : undefined}
            className={cn(
              "block h-12.5 w-full min-w-0 grow rounded-full border bg-white px-3 py-1.5 focus:outline-none",
              className,
              {
                "border-error focus:border-error pr-10": resolvedError,
                "border-greyscale-strength focus:border-primary":
                  !resolvedError,
              },
            )}
            {...rest}
          />
          {resolvedError ? (
            <InfoIcon
              width={20}
              height={20}
              className="text-error pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>
      {resolvedError ? (
        <FieldErrorMessage
          id={id ? `${id}-error` : undefined}
          testId="input-error">
          {resolvedError}
        </FieldErrorMessage>
      ) : null}
    </div>
  );
});

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id?: string;
  name?: string;
  value?: string;
  className?: string;
  label?: string;
  error?: string | boolean;
};
